import CobraSpinner from './CobraSpinner';
import React, { useState, useEffect } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    FormControl,
    FormLabel,
    Textarea,
    RadioGroup,
    Radio,
    Stack,
    useToast,
    VStack,
    HStack,
    Text,
    Badge,
    IconButton,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Box,
    Divider,
    
    Input,
    Heading,
    Icon,
    Container
} from '@chakra-ui/react';
import { DeleteIcon, CheckIcon, CloseIcon } from '@chakra-ui/icons';
import { FiBell, FiUsers, FiBook, FiAlertCircle } from 'react-icons/fi';
import Select from 'react-select';
import alertService from '../services/alertService';
import userService from '../services/userService';
import { fetchCourses } from '../services/courseService';

const AlertManagementModal = ({ isOpen, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [alertsLoading, setAlertsLoading] = useState(false);
    const [alerts, setAlerts] = useState([]);
    const [users, setUsers] = useState([]);
    const [matieres, setMatieres] = useState([]);

    const [message, setMessage] = useState('');
    const [targetType, setTargetType] = useState('matiere');
    const [selectedTarget, setSelectedTarget] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    const toast = useToast();

    useEffect(() => {
        if (isOpen) {
            fetchInitialData();
            fetchAlerts();
        }
    }, [isOpen]);

    const fetchInitialData = async () => {
        try {
            const usersData = await userService.getUsers();
            const userOptions = usersData.map(u => ({
                label: `${u.username} (${u.email})`,
                value: u._id
            }));
            setUsers(userOptions);

            const coursesData = await fetchCourses();
            const courseOptions = coursesData.map(c => ({
                label: c.name,
                value: c.name
            }));
            setMatieres(courseOptions);
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    const fetchAlerts = async () => {
        setAlertsLoading(true);
        try {
            const response = await alertService.getAlerts();
            setAlerts(response.data);
        } catch (error) {
            toast({
                title: 'خطأ في تحميل التنبيهات',
                status: 'error',
                duration: 3000
            });
        } finally {
            setAlertsLoading(false);
        }
    };

    const handleCreateAlert = async () => {
        if (!message || !selectedTarget) {
            toast({
                title: 'يرجى ملء جميع الحقول',
                status: 'warning',
                duration: 3000
            });
            return;
        }

        setLoading(true);
        try {
            const response = await alertService.createAlert({
                message,
                type: targetType,
                target: selectedTarget.value
            });

            toast({
                title: response.message || 'تم إنشاء التنبيه بنجاح',
                description: response.count ? `تم إنشاء ${response.count} تنبيه فردي` : undefined,
                status: 'success',
                duration: 4000
            });
            setMessage('');
            setSelectedTarget(null);
            fetchAlerts();
        } catch (error) {
            toast({
                title: 'فشل في إنشاء التنبيه',
                description: error.response?.data?.message || 'حدث خطأ',
                status: 'error',
                duration: 3000
            });
        } finally {
            setLoading(false);
        }
    };

    const handleToggleStatus = async (alertId) => {
        try {
            await alertService.toggleAlertStatus(alertId);
            fetchAlerts();
            toast({
                title: 'تم تحديث حالة التنبيه',
                status: 'success',
                duration: 2000
            });
        } catch (error) {
            toast({
                title: 'فشل في تحديث التنبيه',
                status: 'error',
                duration: 3000
            });
        }
    };

    const filteredAlerts = alerts.filter(alert =>
        alert.message.toLowerCase().includes(searchQuery.toLowerCase()) ||
        alert.target.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (alert.targetName && alert.targetName.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="full" scrollBehavior="inside">
            <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
            <ModalContent dir="rtl" bg="gray.50" m={0} borderRadius={0}>
                <ModalHeader
                    bg="white"
                    borderBottomWidth="1px"
                    borderColor="gray.200"
                    py={6}
                >
                    <HStack spacing={3}>
                        <Icon as={FiBell} boxSize={7} color="orange.500" />
                        <Heading size="lg" fontFamily="Tajawal, sans-serif" color="gray.800">
                            إدارة التنبيهات الإدارية
                        </Heading>
                    </HStack>
                    <Text fontSize="sm" color="gray.600" mt={2} fontFamily="Tajawal, sans-serif">
                        إرسال وإدارة التنبيهات للطلاب المسجلين قبل 15 ديسمبر 2025
                    </Text>
                </ModalHeader>
                <ModalCloseButton left="20px" right="unset" top="20px" />

                <ModalBody fontFamily="Tajawal, sans-serif" py={8}>
                    <Container maxW="1400px">
                        <VStack spacing={8} align="stretch">
                            {/* Create Alert Section */}
                            <Box
                                p={8}
                                borderWidth="1px"
                                borderRadius="xl"
                                bg="white"
                                boxShadow="sm"
                            >
                                <HStack mb={6} spacing={3}>
                                    <Icon as={FiAlertCircle} boxSize={6} color="blue.500" />
                                    <Heading size="md" fontFamily="Tajawal, sans-serif">
                                        إنشاء تنبيه جديد
                                    </Heading>
                                </HStack>

                                <Stack spacing={6}>
                                    <FormControl isRequired>
                                        <FormLabel fontWeight="600" fontSize="md">نص التنبيه</FormLabel>
                                        <Textarea
                                            placeholder="اكتب رسالة التنبيه هنا..."
                                            value={message}
                                            onChange={(e) => setMessage(e.target.value)}
                                            bg="gray.50"
                                            borderColor="gray.300"
                                            _hover={{ borderColor: 'gray.400' }}
                                            _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)' }}
                                            minH="120px"
                                            fontSize="md"
                                        />
                                    </FormControl>

                                    <FormControl isRequired>
                                        <FormLabel fontWeight="600" fontSize="md">نوع الاستهداف</FormLabel>
                                        <RadioGroup onChange={setTargetType} value={targetType}>
                                            <Stack direction="row" spacing={6}>
                                                <Radio value="matiere" colorScheme="purple" size="lg">
                                                    <HStack spacing={2}>
                                                        <Icon as={FiBook} />
                                                        <Text>حسب المادة (سيتم إنشاء تنبيهات فردية)</Text>
                                                    </HStack>
                                                </Radio>
                                                <Radio value="user" colorScheme="orange" size="lg">
                                                    <HStack spacing={2}>
                                                        <Icon as={FiUsers} />
                                                        <Text>حسب المستخدم</Text>
                                                    </HStack>
                                                </Radio>
                                            </Stack>
                                        </RadioGroup>
                                    </FormControl>

                                    <FormControl isRequired>
                                        <FormLabel fontWeight="600" fontSize="md">
                                            {targetType === 'matiere' ? 'اختر المادة' : 'اختر المستخدم'}
                                        </FormLabel>
                                        <Box color="white">
                                            <Select
                                                options={targetType === 'matiere' ? matieres : users}
                                                value={selectedTarget}
                                                onChange={setSelectedTarget}
                                                placeholder={targetType === 'matiere' ? "اختر مادة..." : "اختر مستخدماً..."}
                                                isRtl={true}
                                                styles={{
                                                    control: (base) => ({
                                                        ...base,
                                                        minHeight: '48px',
                                                        borderColor: '#CBD5E0',
                                                        '&:hover': { borderColor: '#A0AEC0' }
                                                    })
                                                }}
                                            />
                                        </Box>
                                    </FormControl>

                                    <Button
                                        colorScheme="blue"
                                        size="lg"
                                        onClick={handleCreateAlert}
                                        isLoading={loading}
                                        loadingText="جاري الإرسال..."
                                        leftIcon={<Icon as={FiBell} />}
                                        fontWeight="bold"
                                        fontSize="md"
                                    >
                                        إرسال التنبيه
                                    </Button>
                                </Stack>
                            </Box>

                            <Divider />

                            {/* Existing Alerts Section */}
                            <Box
                                p={8}
                                borderWidth="1px"
                                borderRadius="xl"
                                bg="white"
                                boxShadow="sm"
                            >
                                <HStack justify="space-between" mb={6} flexWrap="wrap" gap={4}>
                                    <HStack spacing={3}>
                                        <Icon as={FiUsers} boxSize={6} color="green.500" />
                                        <Heading size="md" fontFamily="Tajawal, sans-serif">
                                            التنبيهات الحالية ({alerts.length})
                                        </Heading>
                                    </HStack>
                                    <Box w={{ base: '100%', md: '350px' }}>
                                        <Input
                                            placeholder="بحث في التنبيهات..."
                                            size="md"
                                            borderRadius="md"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            bg="gray.50"
                                            borderColor="gray.300"
                                            _hover={{ borderColor: 'gray.400' }}
                                            _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)' }}
                                        />
                                    </Box>
                                </HStack>

                                {alertsLoading ? (
                                    <Box textAlign="center" py={12}>
                                        <CobraSpinner size="xl" color="blue.500" thickness="4px" />
                                        <Text mt={4} color="gray.600">جاري تحميل التنبيهات...</Text>
                                    </Box>
                                ) : (
                                    <Box overflowX="auto">
                                        <Table variant="simple" size="md">
                                            <Thead bg="gray.50">
                                                <Tr>
                                                    <Th fontFamily="Tajawal, sans-serif" fontSize="sm">الرسالة</Th>
                                                    <Th fontFamily="Tajawal, sans-serif" fontSize="sm">الهدف</Th>
                                                    <Th fontFamily="Tajawal, sans-serif" fontSize="sm">الحالة</Th>
                                                    <Th fontFamily="Tajawal, sans-serif" fontSize="sm">إجراء</Th>
                                                </Tr>
                                            </Thead>
                                            <Tbody>
                                                {filteredAlerts.map((alert) => (
                                                    <Tr key={alert._id} _hover={{ bg: 'gray.50' }}>
                                                        <Td maxW="400px" isTruncated fontSize="md">
                                                            {alert.message}
                                                        </Td>
                                                        <Td>
                                                            <Badge
                                                                colorScheme={alert.type === 'matiere' ? 'purple' : 'orange'}
                                                                fontSize="sm"
                                                                px={3}
                                                                py={1}
                                                                borderRadius="md"
                                                            >
                                                                {alert.type === 'matiere' ? `مادة: ${alert.target}` : `مستخدم: ${alert.targetName || alert.target}`}
                                                            </Badge>
                                                        </Td>
                                                        <Td>
                                                            <Badge
                                                                colorScheme={alert.active ? 'green' : 'red'}
                                                                fontSize="sm"
                                                                px={3}
                                                                py={1}
                                                                borderRadius="md"
                                                            >
                                                                {alert.active ? 'نشط' : 'غير نشط'}
                                                            </Badge>
                                                        </Td>
                                                        <Td>
                                                            <IconButton
                                                                size="md"
                                                                icon={alert.active ? <CloseIcon /> : <CheckIcon />}
                                                                colorScheme={alert.active ? 'red' : 'green'}
                                                                onClick={() => handleToggleStatus(alert._id)}
                                                                aria-label="Toggle Status"
                                                                variant="ghost"
                                                            />
                                                        </Td>
                                                    </Tr>
                                                ))}
                                                {filteredAlerts.length === 0 && (
                                                    <Tr>
                                                        <Td colSpan={4} textAlign="center" py={12}>
                                                            <Text color="gray.500" fontSize="md">
                                                                {searchQuery ? 'لا توجد تنبيهات تطابق البحث' : 'لا توجد تنبيهات حالياً'}
                                                            </Text>
                                                        </Td>
                                                    </Tr>
                                                )}
                                            </Tbody>
                                        </Table>
                                    </Box>
                                )}
                            </Box>
                        </VStack>
                    </Container>
                </ModalBody>

                <ModalFooter bg="white" borderTopWidth="1px" borderColor="gray.200">
                    <Button onClick={onClose} size="lg" variant="ghost">إغلاق</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default AlertManagementModal;

